import { Avatar } from "@components/ui/Avatar"
import { Card, CardBody } from "@components/ui/Card"
import { useGradeScale } from "@hooks/useGradeScale"
import { cn } from "@lib/cn"
import { formatGrade } from "@lib/intl"
import { ChevronRight, Minus, TrendingDown, TrendingUp } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import type { DirectionAtRiskRow, Trend } from "../types"

import { gradeTier, gradeTierTextClass } from "@/constants/gradeTier"
import type { GradeScale } from "@/types"

interface Props {
  rows: DirectionAtRiskRow[]
  onStudentClick?: (studentId: string) => void
}

// At-risk students — individual avg → "student" tier so a 6.2 doesn't
// read full red.
function gradeColor(avg: number, scale: GradeScale) {
  return gradeTierTextClass(gradeTier(avg, scale, "student"))
}

function TrendIcon({ trend }: { trend: Trend }) {
  if (trend === "improving") return <TrendingUp className="text-success h-3.5 w-3.5" />
  if (trend === "declining") return <TrendingDown className="text-danger-ink h-3.5 w-3.5" />
  return <Minus className="text-ink-subtle h-3.5 w-3.5" />
}

export const AtRiskTopList = memo(function AtRiskTopList({ rows, onStudentClick }: Props) {
  const { t } = useTranslation("direction")
  const scale = useGradeScale()
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-ink text-sm font-semibold">{t("at_risk.title")}</h2>
          <span className="text-ink-subtle text-xs">
            {t("at_risk.count", { count: rows.length })}
          </span>
        </div>
        <ul className="divide-hairline divide-y">
          {rows.map((row) => (
            <li key={row.student_id}>
              <Link
                to={`/salones/${row.classroom_id}/alumnos/${row.student_id}`}
                onClick={() => onStudentClick?.(row.student_id)}
                className="hover:bg-inset flex items-center gap-3 rounded-md p-2 transition-colors focus:ring-2 focus:outline-none"
              >
                <Avatar name={row.name} initials={row.initials} color={row.color} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="text-ink truncate text-sm font-medium">{row.name}</p>
                  <p className="text-ink-subtle truncate text-xs">
                    {t("at_risk.row.subtitle", {
                      classroom: row.classroom_name,
                      professor: row.professor_name,
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {row.consecutive_missed >= 2 && (
                    <span
                      className="bg-danger-soft text-danger-ink inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                      title={t("at_risk.row.missed_tooltip", { count: row.consecutive_missed })}
                    >
                      {t("at_risk.row.missed_short", { count: row.consecutive_missed })}
                    </span>
                  )}
                  <span
                    className={cn(
                      "text-sm font-semibold tabular-nums",
                      gradeColor(row.avg_grade, scale),
                    )}
                  >
                    {formatGrade(row.avg_grade, scale)}
                  </span>
                  <TrendIcon trend={row.trend} />
                  <ChevronRight className="text-ink-subtle h-4 w-4" aria-hidden="true" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
})
